import { useSystemLanguage } from '@contexts/SystemLanguageContext';
import type { BaseUser } from '@core/models/user';
import {
  getDatadogInstance,
  setAnalyticsUser,
  setAnalyticsUserProperties,
} from '@cw-elements/analytics-provider';
import { datadogLogs } from '@datadog/browser-logs';
import { selectSeason } from '@state/season/season.selectors';
import { useBoundStore } from '@state/store';
import { useEffect, useLayoutEffect, useSyncExternalStore } from 'react';
import { validateInternalUser } from 'src/amplitude/utils';
import { isAnalyticsReady, subscribeAnalyticsReady } from 'src/analytics/analyticsLifecycle';
import type { AnalyticsEventContext } from 'src/analytics/eventContext';
import { setAnalyticsEventContext } from 'src/analytics/eventContext';
import { useWorkspace } from './queries/organization/useWorkspace';
import { useOrganization } from './queries/useOrganization';
import { useProperty } from './queries/useProperty';
import { useCookieConsent } from './useCookieConsent';
import { useWorkspaceId } from './useWorkspaceId';

function useAnalyticsIdentity(user?: BaseUser) {
  const [language] = useSystemLanguage();
  const { cookieBotConsentStatistics } = useCookieConsent();
  const { organization } = useOrganization();
  const { property } = useProperty();
  const workspaceId = useWorkspaceId();
  const { data: workspace } = useWorkspace(user ? workspaceId : undefined);
  const analyticsReady = useSyncExternalStore(subscribeAnalyticsReady, isAnalyticsReady);

  // Layout effects run before page-view effects in children. Context is not consent-gated, but
  // still requires a user so cached query data cannot leak across authenticated sessions.
  useLayoutEffect(() => {
    const context: AnalyticsEventContext = {};
    if (user) {
      if (workspaceId) {
        context.workspace_id = workspaceId;
        if (workspace?.id === workspaceId) context.workspace_name = workspace.name;
      }
      if (organization && organization.workspace_id === workspaceId) {
        context.org_id = String(organization.id);
        context.org_name = organization.name;
        context.plan_name = organization.plan ?? 'unknown';
        if (property?.org_id === organization.id) {
          context.property_id = String(property.id);
          context.property_name = property.name;
          context.property_area_hectares = String(property.total_area);
        }
      }
    }
    setAnalyticsEventContext(context);
    return () => setAnalyticsEventContext({});
  }, [user, workspaceId, workspace, organization, property]);
  const selectedSeason = useBoundStore(selectSeason);
  const seasonMode = useBoundStore((state) => state.seasons.mode);

  useEffect(() => {
    const isInternalUser = validateInternalUser(user?.email ?? '');

    if (user && cookieBotConsentStatistics) {
      datadogLogs.setUser({ id: user.id, internal: isInternalUser });
    }

    if (!analyticsReady) return;

    if (user) {
      setAnalyticsUser(user.id);
    }

    if (user?.country_code) {
      getDatadogInstance()?.setGlobalContextProperty('region', user.country_code.toLowerCase());
    }

    // setAnalyticsUserProperties is string-only; coerce non-string values.
    const userProperties: Record<string, string> = {
      internalUser: String(isInternalUser),
      interface_language: language,
    };

    if (user) {
      userProperties.user_id = String(user.id);
    }

    if (user?.email) {
      userProperties.email = user.email;
    }

    if (user?.job_title) {
      userProperties.job_title = user.job_title;
    }

    if (user) {
      if (seasonMode === 'active_seasons') {
        userProperties.crop_id = 'all-active-crop-seasons';
        userProperties.crop_name = 'all-active-crop-seasons';
        userProperties.season_id = 'all-active-crop-seasons';
        userProperties.season_name = 'all-active-crop-seasons';
      } else if (selectedSeason) {
        const { season } = selectedSeason;
        userProperties.crop_id = season.catalogCrop?.id ?? 'unknown';
        userProperties.crop_name = season.catalogCrop?.adapted_wk_slug ?? 'unknown';
        userProperties.season_id = season.id;
        userProperties.season_name = season.name;
      }
    }

    setAnalyticsUserProperties(userProperties);
  }, [user, cookieBotConsentStatistics, language, seasonMode, selectedSeason, analyticsReady]);
}

export { useAnalyticsIdentity };
